import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, DollarSign, FileText, AlertTriangle, RefreshCcw, Truck, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import LocalizedNumberInput from '../components/ui/LocalizedNumberInput';
import { parseLocalizedNumber } from '../utils/numberFormat';
import { buildLocalOperationTimestamp, getLocalDateKey } from '../utils/localDateTime';

const fmtMoney = (value) =>
  Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ProveedoresMovimiento = ({ id, onNavigate }) => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const proveedorId = id || routeId;

  const [prov, setProv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tipo: 'Nota de Debito', monto: '', obs: '', fecha_flujo: getLocalDateKey(), proyectar_flujo: true });

  const goToDetalle = () => {
    if (onNavigate) return onNavigate('proveedores-detalle', proveedorId);
    navigate(`/sistema/proveedores-detalle/${proveedorId}`);
  };

  useEffect(() => {
    if (!proveedorId || !profile?.organization_id) return;
    loadProveedor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveedorId, profile?.organization_id]);

  const loadProveedor = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('proveedores')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .eq('id', proveedorId)
        .single();

      if (error) throw error;
      setProv(data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar proveedor');
      if (onNavigate) onNavigate('proveedores');
      else navigate('/sistema/proveedores');
    } finally {
      setLoading(false);
    }
  };

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const monto = parseLocalizedNumber(form.monto);
      if (!monto || monto <= 0) {
        toast.error('Ingresá un monto valido');
        return;
      }

      const impacto = form.tipo === 'Nota de Debito' || form.tipo === 'Ajuste (+)' ? monto : -monto;
      const saldoActual = Number(prov?.saldo || 0);
      const saldoNuevo = saldoActual + impacto;
      const fecha = buildLocalOperationTimestamp(form.fecha_flujo || getLocalDateKey());

      const { error: errorProv } = await supabase
        .from('proveedores')
        .update({ saldo: saldoNuevo })
        .eq('id', proveedorId);

      if (errorProv) throw errorProv;

      const { error: errorMov } = await supabase
        .from('proveedor_movimientos')
        .insert([
          {
            proveedor_id: proveedorId,
            organization_id: profile.organization_id,
            tipo: form.tipo,
            monto: impacto,
            saldo_resultante: saldoNuevo,
            observaciones: form.obs?.trim() || null,
            fecha,
          }
        ]);

      if (errorMov) throw errorMov;

      if (form.proyectar_flujo) {
        const proyTipo = impacto >= 0 ? 'egreso' : 'ingreso';
        const proyConcepto = `${form.tipo} proveedor ${prov?.razon_social || ''}${form.obs ? ` - ${form.obs}` : ''}`.trim();
        const { error: proyError } = await supabase.from('proyecciones').insert([{
          organization_id: profile.organization_id,
          concepto: proyConcepto.toUpperCase(),
          monto: Math.abs(impacto),
          fecha: form.fecha_flujo || getLocalDateKey(),
          tipo: proyTipo,
          categoria: 'Cuenta Corriente',
          sitio_id: null,
        }]);
        if (proyError) {
          console.warn('No se pudo proyectar ajuste de proveedor en flujo de fondos:', proyError);
          toast('El ajuste se guardo, pero no se pudo crear la proyeccion de flujo.');
        }
      }

      toast.success('Movimiento impactado correctamente');
      goToDetalle();
    } catch (error) {
      console.error(error);
      toast.error('Error al guardar el movimiento');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={34} />
        <p className="font-black uppercase tracking-[0.25em] text-slate-400 text-[10px]">Auditando legajo...</p>
      </div>
    );
  }

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3 px-1">
        <div className="flex items-center gap-3">
          <button
            onClick={goToDetalle}
            className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Truck size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-800 tracking-tight uppercase">Movimiento Manual</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Ajuste de cuenta corriente para {prov?.razon_social || 'proveedor'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5 mb-3">
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Proveedor</p>
            <Truck size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight truncate">{prov?.razon_social || '-'}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Legajo activo</p>
        </div>
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Saldo actual</p>
            <DollarSign size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight">$ {fmtMoney(prov?.saldo)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Antes del ajuste</p>
        </div>
        <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-sm sm:col-span-2 xl:col-span-1">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Operacion</p>
            <FileText size={16} className="text-slate-300" />
          </div>
          <p className="mt-1 text-lg font-black text-slate-800 tracking-tight">{form.tipo}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Impacto manual sobre la cuenta</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-slate-50/50 px-4 py-3 sm:px-5 border-b border-slate-100 flex items-center gap-3">
          <FileText size={16} className="text-[#51A7FD]" />
          <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-500">Carga de movimiento</h3>
        </div>

        <form onSubmit={guardar} className="p-4 sm:p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600 shrink-0">
              <AlertTriangle size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase text-amber-800 tracking-widest mb-1">Aviso de impacto contable</p>
              <p className="text-[11px] font-medium text-amber-700 leading-relaxed">
                Las notas de debito aumentan la deuda y las notas de credito la reducen. Los ajustes manuales impactan de forma inmediata.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Tipo de operacion</label>
              <select
                className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-[11px] font-black uppercase outline-none focus:border-[#51A7FD]"
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
              >
                <option value="Nota de Debito">Nota de Debito (Suma deuda)</option>
                <option value="Nota de Credito">Nota de Credito (Resta deuda)</option>
                <option value="Ajuste (+)">Ajuste manual (+)</option>
                <option value="Ajuste (-)">Ajuste manual (-)</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Importe total</label>
              <div className="relative">
                <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <LocalizedNumberInput
                  decimals={2}
                  selectOnFocus
                  className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 pl-10 pr-4 text-[11px] font-black text-slate-900 outline-none focus:border-[#51A7FD]"
                  placeholder="0,00"
                  value={form.monto}
                  onValueChange={(display) => setForm({ ...form, monto: display })}
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Fecha en flujo</label>
              <div className="relative">
                <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                  type="date"
                  className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 pl-10 pr-4 text-[11px] font-black text-slate-900 outline-none focus:border-[#51A7FD]"
                  value={form.fecha_flujo}
                  onChange={(e) => setForm({ ...form, fecha_flujo: e.target.value })}
                />
              </div>
            </div>

            <label className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 mt-0 md:mt-5 cursor-pointer">
              <input
                type="checkbox"
                checked={form.proyectar_flujo}
                onChange={(e) => setForm({ ...form, proyectar_flujo: e.target.checked })}
                className="h-4 w-4 accent-[#3FF2AA]"
              />
              <span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Ver en flujo de fondos</span>
            </label>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1 block mb-1">Observaciones</label>
            <textarea
              className="w-full bg-slate-50 border border-slate-100 rounded-xl p-4 text-[11px] font-bold text-slate-600 outline-none focus:border-[#51A7FD] h-28 resize-none"
              placeholder="Ej: Ajuste por diferencia de saldo..."
              value={form.obs}
              onChange={(e) => setForm({ ...form, obs: e.target.value })}
            />
          </div>

          <Button
            type="submit"
            isLoading={saving}
            className="w-full h-12 bg-[#0B2A36] text-white rounded-2xl shadow-lg hover:scale-[1.01] transition-all active:scale-95 flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] disabled:opacity-50"
          >
            Confirmar movimiento
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ProveedoresMovimiento;
